import { useEffect, useRef, useState } from 'react';
import { Avatar, type AvatarProps } from '~/components/ui/Avatar';
import { Button } from '~/components/ui/Button';
import { Dialog, DialogDescription, DialogTitle, type DialogProps } from '~/components/ui/LegacyDialog';
import { useGithubToken, userData, type GetGithubToken } from '~/lib/client-github';
import { getReference } from '~/lib/github-api';
import type { RepoFullName, Target } from '~/types/github';
import { classNames } from '~/utils/classNames';
import { debounce } from '~/utils/debounce';
import { useInitRef } from '~/utils/init-ref';
import { NOOP } from '~/utils/noops';

const NAME_CHECK_DEBOUNCE = 300;

type BranchError =
  | {
      kind: 'taken';
    }
  | {
      kind: 'creation';
      message: string;
    };

export function AddBranchDialog({
  open,
  repository,
  username,
  avatarUrl,
  onClose,
  onChoose,
  currentBranch,
}: {
  username: string;
  avatarUrl?: string;
  repository: RepoFullName;
  currentBranch: string;
  open: boolean;
  onClose: DialogProps['onClose'];
  onChoose: (branch: string) => Promise<{ message: string } | undefined>;
}) {
  const [branch, setBranch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<BranchError>();

  const checkRef = useRef<{ task: Promise<boolean | null>; branchName: string }>();

  const getGithubToken = useGithubToken();

  const debouncedRef = useInitRef(() =>
    debounce((target: Target, currentBranch: string, signal: AbortSignal) => {
      checkRef.current = {
        task: checkNameIsTaken(getGithubToken, target, currentBranch, signal),
        branchName: target.branchName,
      };

      checkRef.current?.task.then((taken) => {
        if (taken) {
          setError({ kind: 'taken' });
        }
      });
    }, NAME_CHECK_DEBOUNCE),
  );

  const nameCheckAfterInput = debouncedRef.current;

  useEffect(() => {
    if (!branch) {
      return NOOP;
    }

    const controller = new AbortController();

    nameCheckAfterInput({ repository, branchName: branch }, currentBranch, controller.signal);

    return () => controller.abort();
  }, [branch, repository, currentBranch]);

  async function chooseBranch() {
    setBusy(true);

    const check =
      checkRef.current?.branchName === branch
        ? checkRef.current.task
        : checkNameIsTaken(getGithubToken, { repository, branchName: branch }, currentBranch);

    const taken = await check;

    if (taken) {
      setError({ kind: 'taken' });
      setBusy(false);

      return;
    }

    try {
      const error = await onChoose(branch);

      if (error) {
        setError({ kind: 'creation', message: error.message });
      }
    } finally {
      setBusy(false);
    }
  }

  function updateBranch(branch: string) {
    setBranch(branch);
    setError(undefined);
  }

  return (
    <AddBranchDialogControlled
      {...{ branch, currentBranch, busy, error, repository, open, username }}
      owner={{ name: repository.owner, avatar: avatarUrl }}
      onChange={updateBranch}
      onClose={onClose}
      onChoose={chooseBranch}
    />
  );
}

export function AddBranchDialogControlled({
  open,
  busy,
  repository,
  branch,
  currentBranch,
  owner,
  onClose,
  onChoose,
  onChange,
  username,
  error,
}: {
  username: string;
  repository: RepoFullName;
  branch: string;
  currentBranch: string;
  busy: boolean;
  onChange: (branch: string) => void;
  open: boolean;
  owner: AvatarProps['user'];
  onClose: DialogProps['onClose'];
  onChoose: () => void;
  error?: BranchError;
}) {
  return (
    <Dialog onClose={onClose} open={open} maxWidth={600}>
      <DialogTitle className="p-6 font-bold text-lg border-b-1px border-bolt-elements-borderColor no-outline">
        <span className="flex items-center gap-1 text-lg">
          <span className="i-bolt:logos-github?mask text-2xl"></span>
          <span className="font-semibold">Add a branch</span>
        </span>
      </DialogTitle>
      <DialogDescription>
        <div className="flex flex-col w-full items-start">
          <label
            htmlFor="github-add-branch-dialog-branch-name-input"
            className="text-bolt-elements-textSecondary mb-3 text-sm"
          >
            Choose a name for your branch
          </label>

          <div className="flex gap-5 w-full justify-between items-center mb-1">
            <Avatar user={userData(owner, username)} />

            <code className="inline-block">
              {repository.owner}
              <span className="inline-block w-3" />/
              <span className="inline-block w-3" />
              {repository.name}
              <span className="inline-block w-3" />/
            </code>

            <input
              id="github-add-branch-dialog-branch-name-input"
              className="grow rounded outline-none bg-bolt-elements-background-depth-4 p-6px pl-2 light:placeholder:text-black/30 dark:placeholder:text-white/30"
              placeholder="my-branch-name"
              type="text"
              value={branch}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onChoose()}
            />
          </div>

          <p
            className={classNames('text-right w-full text-sm mb-8', {
              'text-bolt-elements-item-contentDanger': error != null,
              'text-bolt-elements-textSecondary': error == null,
            })}
          >
            {error?.kind === 'taken' && `${branch} already exists, please choose a different branch name`}
            {error?.kind === 'creation' && error.message}
            {!error && `You branch will start from ${currentBranch}`}
          </p>

          <div className="flex w-full justify-between items-center">
            <div className="text-sm text-bolt-elements-textTertiary">
              New to GitHub?{' '}
              <a className="text-inherit!" href="https://support.bolt.new/integrations/git" target="_blank">
                Learn more
              </a>
            </div>
            <Button size="lg" disabled={!branch || error != null || busy} onClick={onChoose} variant="primary">
              <div className="flex items-center">
                {busy && <div className="i-svg-spinners:90-ring-with-bg size-4 mr-1" />}
                <div>{busy ? 'Creating branch' : 'Create branch'}</div>
              </div>
            </Button>
          </div>
        </div>
      </DialogDescription>
    </Dialog>
  );
}

// might return false negatives
async function checkNameIsTaken(
  getGithubToken: GetGithubToken,
  target: Target,
  currentBranch: string,
  signal?: AbortSignal,
) {
  if (signal?.aborted) {
    return null;
  }

  if (target.branchName === currentBranch) {
    return true;
  }

  const token = await getGithubToken();

  if (!token) {
    return false;
  }

  if (signal?.aborted) {
    return null;
  }

  try {
    const ref = await getReference(target, token);

    if (signal?.aborted) {
      return null;
    }

    return ref != null;
  } catch {
    return false;
  }
}
